import React, { ChangeEvent, FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicingState, Order } from 'models/order';
import { Alert, Box, Button, Link, TextField } from '@mui/material';
import CreateInvoiceButton from './create-invoice-button.component';
import OrderInvoiceStateButtons from './order-invoice-state-buttons.component';
import InvoiceInfo from './invoice-info.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { LabClient } from 'models/client-directory';
import { InvoiceDelivery, InvoiceType } from 'models/invoice';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
    toggleOrderDoNotInvoiceTemplate,
    toggleOrderReadyForInvoiceTemplate,
} from 'apollo/templates/invoices/mutation.graphql';
import { saveInvoiceNumberTemplate } from 'apollo/templates/order';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import CreatePerOrderInvoiceDialog from 'components/order-review/create-per-order-invoice-dialog/create-per-order-invoice-dialog.component';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import { Link as RouterLink } from 'react-router-dom';
import MissingCustomerNumberDialog from 'pages/manage-invoices/missing-customer-number-dialog/missing-customer-number-dialog.component';
import { hasPtjCustomerNumber } from 'utils/organizations';
import ErrorDialog from 'components/shared/error-dialog/error-dialog.component';
import { getParentChildLabClientComboTemplate } from 'apollo/templates/client-directory/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import { resolveClinicInvoiceSettings } from 'utils/invoice-settings';
import { useRefreshOrder } from 'hooks/useRefreshOrder';

type InvoiceActionsProps = {
    order: Order;
};
const InvoiceActions: FC<InvoiceActionsProps> = ({ order }: InvoiceActionsProps) => {
    const refreshOrder = useRefreshOrder();
    const { t: translate } = useTranslation(['invoices', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();

    const currentOrg = currentOrgVar();

    const [invoiceType, setInvoiceType] = useState('');
    const [invoiceDelivery, setInvoiceDelivery] = useState('');
    const [showInvoicingInfo, setShowInvoicingInfo] = useState(false);
    const [invoiceState, setInvoiceState] = useState<InvoicingState>(InvoicingState.NOT_READY);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [error, setError] = useState<Error | null>(null);
    const [isManual, setIsManual] = useState(false);
    const [createInvoiceDialogOpen, setCreateInvoiceDialogOpen] = useState(false);
    const [destInvoiceEmail, setDestInvoiceEmail] = useState<string>();
    const [isChangedInvoiceNumber, setIsChangedInvoiceNumber] = useState(false);
    const [isPtjOrg, setIsPtjOrg] = useState(false);
    const [showMissingCustomerNumberDialog, setShowMissingCustomerNumberDialog] = useState(false);
    const [parentClientSettings, setParentClientSettings] = useState<LabClient>({} as LabClient);
    const [childClientSettings, setChildClientSettings] = useState<LabClient>({} as LabClient);

    const { loading: parentChildLabClientComboLoading } = useQuery(getParentChildLabClientComboTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            parentId: order?.customer?._id,
            childId: order?.deliveryDetails?.destination?._id,
        },
        onCompleted: ({ parentChildLabClientCombo }: { parentChildLabClientCombo: LabClient[] }) => {
            const [parentClientSettings, childClientSettings] = parentChildLabClientCombo;
            if (parentClientSettings?.invoiceEnabled) {
                setParentClientSettings(parentClientSettings);
            }
            if (childClientSettings?.invoiceEnabled) {
                setChildClientSettings(childClientSettings);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (order && (parentClientSettings?.customerId || childClientSettings?.customerId)) {
            setInvoiceTypeAndDelivery();
            if (order.invoice) {
                setInvoiceState(InvoicingState.READY_FOR_INVOICE);
                setShowInvoicingInfo(false);
            } else {
                setInvoiceState(order.invoicingState || InvoicingState.NOT_READY);
            }
            // A live order update (DD-3231) re-runs this; keep an invoice number being typed.
            if (!isChangedInvoiceNumber) {
                setInvoiceNumber(order.tempInvoiceNumber as string);
            }
            setIsManual(currentOrg?.invoicing?.configuration?.invoiceNumberType === 'MANUAL');
            const isPtjOrg = customerSettings.getCustomerSetting(
                currentOrg?.customerSettings,
                CONSTANTS.SETTINGS_NAMES.PTJ_ORGANIZATION,
            );
            setIsPtjOrg(isPtjOrg);
        }
    }, [order, parentClientSettings, childClientSettings]);

    const setInvoiceTypeAndDelivery = () => {
        const { clinicType, clinicDelivery, activeClientSettings } = resolveClinicInvoiceSettings(
            order,
            parentClientSettings,
            childClientSettings,
            currentOrg,
        );

        const type = clinicType;
        const delivery = clinicDelivery;

        if (type) {
            setInvoiceType(type);
        }
        if (delivery) {
            setDestInvoiceEmail(order.customer?.invoicing?.communication?.email || '');
            setInvoiceDelivery(delivery);
        }

        if (activeClientSettings.invoiceEnabled) {
            setShowInvoicingInfo(true);
            setDestInvoiceEmail(activeClientSettings?.invoiceSettings?.emailSettings?.to?.join(', ') || '');
            if (!type) {
                setInvoiceType(activeClientSettings.invoiceSettings?.type as string);
            }
            if (!delivery) {
                setInvoiceDelivery(activeClientSettings.invoiceSettings?.invoiceDelivery as string);
            }
        } else {
            setShowInvoicingInfo(false);
        }
    };

    const [saveInvoiceNumber] = useMutation(saveInvoiceNumberTemplate, {
        onCompleted: () => {
            setIsChangedInvoiceNumber(false);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('invoices:INVOICE_NUMBER_SAVED'),
            });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [toggleOrderReadyForInvoice] = useMutation(toggleOrderReadyForInvoiceTemplate, {
        // Returns a ReviewOrder, not the Order, so the order is re-read rather than patched.
        onCompleted: () => {
            refreshOrder(order._id);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('invoices:INVOICE_STATE_SAVED'),
            });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [toggleOrderDoNotInvoice] = useMutation(toggleOrderDoNotInvoiceTemplate, {
        onCompleted: () => {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('invoices:INVOICE_STATE_SAVED'),
            });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const onInvoiceSelection = async (value: string) => {
        setInvoiceState(InvoicingState[value as keyof typeof InvoicingState]);
        setError(null);
        if (value !== InvoicingState.SHOULD_NOT_INVOICE) {
            await toggleOrderReadyForInvoice({
                variables: {
                    orderId: order._id,
                    invoiceAction: value,
                },
            });
        } else {
            await toggleOrderDoNotInvoice({
                variables: {
                    orderId: order._id,
                    isDoNotInvoice: true,
                },
            });
        }
    };

    const handleInvoiceNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsChangedInvoiceNumber(order.tempInvoiceNumber !== event.target.value);
        setInvoiceNumber(event.target.value);
    };

    const handleSaveInvoiceNumber = async () => {
        setError(null);
        await saveInvoiceNumber({
            variables: {
                orderId: order._id,
                invoiceNumber: invoiceNumber,
            },
        });
    };

    const handleToggleOrderDoNotInvoice = async (isDoNotInvoice: boolean) => {
        setError(null);
        await toggleOrderDoNotInvoice({
            variables: {
                orderId: order._id,
                isDoNotInvoice: isDoNotInvoice,
            },
        });
    };

    const handleCreateInvoice = () => {
        if (isPtjOrg) {
            if (!hasPtjCustomerNumber(order, parentClientSettings)) {
                // open dialog here
                setShowMissingCustomerNumberDialog(true);
                return;
            }
        }
        setError(null);
        setCreateInvoiceDialogOpen(true);
    };

    const closeDialog = () => {
        setCreateInvoiceDialogOpen(false);
    };

    const handleInvoiceCreation = () => {
        // Invoicing changes the order server-side; re-read it instead of guessing its fields.
        refreshOrder(order._id);
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('invoices:INVOICE_STATE_SAVED'),
        });
    };

    if (parentChildLabClientComboLoading) {
        return <Loader />;
    }

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            {order.invoice ? (
                <Box>
                    <InvoiceInfo order={order} />
                </Box>
            ) : undefined}
            {order.isImportedOrder && (
                <Box display="flex" alignItems="center">
                    <Box ml={1} flex="1 0 auto">
                        {translate('orders:DO_NOT_INVOICE')}
                    </Box>
                    <DoDisturbIcon color="success" fontSize="medium" />
                </Box>
            )}
            {!order.isImportedOrder && showInvoicingInfo && invoiceType === InvoiceType.SINGLE ? (
                <Fragment>
                    {isManual && order.invoicingState !== InvoicingState.SHOULD_NOT_INVOICE ? (
                        <Fragment>
                            <Box>
                                <TextField
                                    required
                                    fullWidth
                                    id="invoice-number"
                                    margin="dense"
                                    value={invoiceNumber}
                                    onChange={handleInvoiceNumberChange}
                                    label={translate('invoices:INVOICE_NUMBER')}
                                    data-test="invoice-actions.invoice-number"
                                />
                            </Box>
                            <Box mb={1}>
                                <Button
                                    fullWidth
                                    disabled={!invoiceNumber || !isChangedInvoiceNumber}
                                    onClick={handleSaveInvoiceNumber}
                                    data-test="invoice-actions.save-invoice-number"
                                >
                                    {translate('invoices:SAVE_INVOICE_NUMBER')}
                                </Button>
                            </Box>
                        </Fragment>
                    ) : undefined}

                    {order.invoicingState === InvoicingState.SHOULD_NOT_INVOICE ? (
                        <Box display="flex" alignItems="center">
                            <Box ml={1} flex="1 0 auto">
                                {translate('orders:DO_NOT_INVOICE')}
                            </Box>
                            <DoDisturbIcon color="success" fontSize="medium" />
                        </Box>
                    ) : undefined}

                    <Box>
                        <CreateInvoiceButton
                            disabled={isChangedInvoiceNumber}
                            order={order}
                            isManual={isManual}
                            onToggleOrderDoNotInvoice={handleToggleOrderDoNotInvoice}
                            onCreateInvoice={handleCreateInvoice}
                        />
                    </Box>
                </Fragment>
            ) : undefined}
            {!order.isImportedOrder &&
            showInvoicingInfo &&
            (invoiceType === InvoiceType.COLLECTIVE_INVOICE || invoiceType === InvoiceType.COLLECTIVE_DENTIST) ? (
                <Box>
                    <OrderInvoiceStateButtons
                        order={order}
                        invoiceState={invoiceState}
                        onChangeInvoice={onInvoiceSelection}
                    />
                </Box>
            ) : undefined}

            {!showInvoicingInfo && !order.invoice ? (
                <Alert severity="info">
                    <Box>{translate('invoices:INVOICE_NOT_ENABLED_FOR_CUSTOMER')}</Box>
                    <Box>
                        <Link component={RouterLink} to={`/client-directory/${order.customer._id}/invoices#content`}>
                            {translate('invoices:SHOW_SETTINGS')}
                        </Link>
                    </Box>
                </Alert>
            ) : undefined}

            <CreatePerOrderInvoiceDialog
                open={createInvoiceDialogOpen}
                onCreateInvoice={handleInvoiceCreation}
                onDialogClose={closeDialog}
                orderId={order._id}
                shouldSendEmail={invoiceDelivery === 'EMAIL'}
                destInvoiceEmail={invoiceDelivery === 'EMAIL' ? destInvoiceEmail || '' : ''}
                invNumber={order.tempInvoiceNumber || ''}
                clinicInvoiceDeliveryMethod={invoiceDelivery || ''}
            />
            <MissingCustomerNumberDialog
                open={showMissingCustomerNumberDialog}
                onClose={() => {
                    setInvoiceState(InvoicingState.NOT_READY);
                    setShowMissingCustomerNumberDialog(false);
                }}
            />
        </Fragment>
    );
};

export default InvoiceActions;
